import { ExceptionUtils } from "#src/utils/exception";
import type { UserInterfaceConfigurationType } from "@validereinc/domain";
import {
  UserInterfaceConfigurationAdapter,
  UserInterfaceConfigurationVisibility,
} from "@validereinc/domain";

type LandingPageConfigurationType = {
  "redirect-to": string;
};

// Helper function to find the default configuration or use the first active one
const pickDefaultOrFirst = (list: UserInterfaceConfigurationType[]) =>
  list.find((c) => c?.is_default) ?? list?.[0];

/**
 * Fetches the user's custom landing page redirect configuration
 * @param userId - The user's ID
 * @returns The redirect path if configured, otherwise null
 */
export const fetchUserLandingPageRedirect = async (
  userId: string
): Promise<string | null> => {
  try {
    const response = await UserInterfaceConfigurationAdapter.getList({
      filters: {
        type: "landing-page",
        scope: "multi-view",
        status: "active",
        //@ts-expect-error: This is a valid key:
        isAlreadyFormatted: true,
      },
    });

    const configurations = response?.data ?? [];

    const personalConfigs = configurations.filter(
      (config: UserInterfaceConfigurationType) =>
        config.visibility === UserInterfaceConfigurationVisibility.PERSONAL &&
        config.owner_id === userId
    );
    const companyConfigs = configurations.filter(
      (config: UserInterfaceConfigurationType) =>
        config.visibility === UserInterfaceConfigurationVisibility.COMPANY
    );

    // Personal overrides company: prefer personal config, then fall back to company
    const landingPageConfig =
      pickDefaultOrFirst(personalConfigs) ?? pickDefaultOrFirst(companyConfigs);

    if (landingPageConfig?.configuration) {
      const config =
        landingPageConfig.configuration as LandingPageConfigurationType;
      const redirectPath = config["redirect-to"];

      if (redirectPath && typeof redirectPath === "string") {
        return redirectPath;
      }
    }

    return null;
  } catch (err) {
    ExceptionUtils.reportException(err, "warning", {
      hint: "Failed to fetch user landing page configuration",
      sourceComponent: "LoginCallback",
    });
    return null;
  }
};
